import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Building2, CheckCircle, XCircle } from 'lucide-react';
import axios from '../api/axios';

const Login = () => {
  const [companyCode, setCompanyCode] = useState(() => localStorage.getItem('lastCompanyCode') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyValid, setCompanyValid] = useState(null); // null = not checked, true/false
  const { login, user } = useAuth();

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/" replace />;
  }

  // Validate company code on blur
  const validateCompanyCode = async (code) => {
    if (!code || code.trim().length < 2) {
      setCompanyValid(null);
      setCompanyName('');
      return;
    }

    try {
      const response = await axios.get(`/api/tenants/validate/${code.trim().toLowerCase()}`);
      if (response.data.valid) {
        setCompanyValid(true);
        setCompanyName(response.data.name);
      } else {
        setCompanyValid(false);
        setCompanyName('');
      }
    } catch (e) {
      // If endpoint doesn't exist (single-tenant mode), treat as valid
      setCompanyValid(null);
      setCompanyName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password, companyCode.trim().toLowerCase() || undefined);
    
    if (!result.success) {
      setError(result.error);
    } else {
      // Remember company code for next login
      if (companyCode.trim()) {
        localStorage.setItem('lastCompanyCode', companyCode.trim().toLowerCase());
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            AGMS Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Auto Garage Management System
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-3">
            {/* Company Code */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={companyCode}
                onChange={(e) => {
                  setCompanyCode(e.target.value);
                  setCompanyValid(null);
                  setCompanyName('');
                }}
                onBlur={(e) => validateCompanyCode(e.target.value)}
                className="relative block w-full pl-10 pr-10 px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Company Code (e.g., alfalah)"
                autoComplete="organization"
              />
              {companyValid !== null && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {companyValid ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {companyName && (
              <p className="text-xs text-green-600 pl-1">✓ {companyName}</p>
            )}
            {companyValid === false && (
              <p className="text-xs text-red-600 pl-1">Invalid company code</p>
            )}

            {/* Email */}
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="relative block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Email address"
                autoComplete="email"
                required
              />
            </div>

            {/* Password */}
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="relative block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Password"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center p-2 bg-red-50 rounded">{error}</div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || companyValid === false}
              className="group relative w-full flex justify-center items-center py-3 sm:py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 touch-target"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <p className="text-xs text-center text-gray-500">
            Leave company code empty for default company
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
